import React, {useCallback, useEffect, useMemo} from 'react';
import {CircularProgress, makeStyles} from '@material-ui/core';
import {Subtitle, UploadDraggableArea} from 'admin/components';
import {Container} from 'components-lib';
import {SingleFileUpload} from 'admin/resources/upload/components';
import {FormNameEnum} from '../../../enums';
import {useFormikContext} from 'formik';
import {IUploadFileFormBaseValues} from 'admin/resources/upload';
import {ALLOWED_MIMETYPE_FORMATS, getFileName, getFileType, mapFileTypeNumber} from 'admin/resources/upload/utils';
import {FileSelectionLoadingProvider, useFileSelectionLoading} from 'hooks';

export const UploadFileContentStart = () => {
  const allowedFormats = useMemo(() => ALLOWED_MIMETYPE_FORMATS.join(', '), []);

  return (
    <FileSelectionLoadingProvider name={FormNameEnum.file} accept={allowedFormats}>
      <UploadFileContentStartInner allowedFormats={allowedFormats} />
    </FileSelectionLoadingProvider>
  );
};

interface IUploadFileContentStartInnerProps {
  allowedFormats: string;
}

const UploadFileContentStartInner = ({allowedFormats}: IUploadFileContentStartInnerProps) => {
  const classes = useStyles();
  const {
    values: {file, displayName},
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<IUploadFileFormBaseValues>();
  const fileSelectionLoading = useFileSelectionLoading();
  const isApplyingFile = !!fileSelectionLoading?.isApplyingFile;
  const fileName = useMemo(() => (file ? getFileName(file.name) : ''), [file]);

  const setFileType = useCallback(
    (fileTypeNum: number) => {
      setFieldValue(FormNameEnum.fileType, fileTypeNum ?? null);
      // (setFieldTouched) See: https://github.com/formium/formik/issues/456
      setFieldTouched(FormNameEnum.fileType, true, true);
      setFieldValue(FormNameEnum.shouldDisableFileType, true);
    },
    [setFieldValue, setFieldTouched]
  );

  const deleteFileType = useCallback(() => {
    setFieldValue(FormNameEnum.fileType, null);
    setFieldValue(FormNameEnum.displayName, null);
    setFieldValue(FormNameEnum.fileName, null);
    setFieldValue(FormNameEnum.shouldDisableFileType, false);
  }, [setFieldValue]);

  useEffect(() => {
    if (!!file) {
      if (!displayName) {
        setFieldValue(FormNameEnum.displayName, fileName);
        setFieldTouched(FormNameEnum.displayName, true, true);
      }

      setFieldValue(FormNameEnum.fileName, file.name);

      const fileType = file && getFileType(file.type, file.name);
      const fileTypeNum = mapFileTypeNumber(fileType);

      // Map File type number based on the string

      if (!!fileTypeNum) {
        setFileType(fileTypeNum);
      }
    }
  }, [file, setFieldValue, setFieldTouched, fileName, setFileType, displayName]);

  return (
    <UploadDraggableArea name={FormNameEnum.file}>
      <Container disableGutters classes={{root: classes.section}}>
        <Subtitle withColor={false} as="h3" color="textPrimary" title="Upload File" />
        <SingleFileUpload
          name={FormNameEnum.file}
          accept={allowedFormats}
          deleteCallback={deleteFileType}
          disableDragAndDrop
        />
        {isApplyingFile && (
          <div className={classes.loadingOverlay} aria-busy="true" aria-live="polite">
            <CircularProgress size={48} color="primary" />
          </div>
        )}
      </Container>
    </UploadDraggableArea>
  );
};

const useStyles = makeStyles((theme) => ({
  section: {
    position: 'relative',
    width: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    backgroundColor: theme.palette.common.white,
  },
}));
